import { SCREEN_BREAKPOINT, useScreenBreakpoint } from '@allesauto/utils/hooks';
import { ArrowLeft16, ArrowRight16 } from '@carbon/icons-react';
import clsx from 'clsx';
import React, {
  FC,
  MouseEvent,
  MouseEventHandler,
  useCallback,
  useMemo,
} from 'react';

import './pagination.css';

export interface PaginationProps {
  pagesCount: number;
  activePage: number;
  pagesVisibleCountMd?: number;
  pagesVisibleCountLg?: number;
  onPageClick?: (page: number) => void;
  prevLinkText?: string;
  nextLinkText?: string;
  navigatorText?: string;
}

const Ellipsis = () => (
  <span className="bg-white cursor-default flex flex-1 items-center justify-center min-w-[60px] ml-px p-20px text-purple80 monochrome:text-grey90">
    ...
  </span>
);

const PageLink = ({
  page,
  isActive = false,
  className,
  onClick,
}: {
  page: number;
  isActive?: boolean;
  className: string;
  onClick: MouseEventHandler<HTMLButtonElement>;
}) => {
  return (
    <button
      {...(isActive ? { 'aria-current': 'page' } : {})}
      className={className}
      onClick={onClick}
    >
      {page}
    </button>
  );
};

const Pagination: FC<PaginationProps> = (props) => {
  const {
    pagesCount,
    pagesVisibleCountMd = 7,
    pagesVisibleCountLg = 11,
    onPageClick,
    prevLinkText = 'Zurück',
    nextLinkText = 'Weiter',
    navigatorText = 'von',
  } = props;
  const breakpoint = useScreenBreakpoint();

  let { activePage } = props;

  // make sure we have a valid activePage value
  if (activePage < 1) {
    activePage = 1;
  } else if (activePage > pagesCount) {
    activePage = pagesCount;
  }

  let pagesVisibleCount =
    breakpoint < SCREEN_BREAKPOINT.LG
      ? pagesVisibleCountMd
      : pagesVisibleCountLg;

  // make sure we have a valid pagesVisibleCount value, cos the min amount of visible pages is 5
  if (pagesVisibleCount < 5) {
    pagesVisibleCount = 5;
  }

  const { pages, truncateStart, truncateEnd } = useMemo(() => {
    let pages = Array.from({ length: pagesCount }, (_, pi) => ++pi);
    let truncateStart = false;
    let truncateEnd = false;

    if (pagesCount > pagesVisibleCount) {
      const halfVisible = Math.floor(pagesVisibleCount / 2);

      truncateStart = activePage > halfVisible + 1;
      truncateEnd = activePage < pagesCount - halfVisible;

      if (truncateStart && truncateEnd) {
        const halfSpanVisible = Math.floor((pagesVisibleCount - 4) / 2);
        const activePageIndex = pages.indexOf(activePage);

        pages = pages.slice(
          activePageIndex - halfSpanVisible,
          activePageIndex + halfSpanVisible + (pagesVisibleCount % 2 ? 1 : 0)
        );
      } else if (truncateStart) {
        pages = pages.slice(-(pagesVisibleCount - 2));
      } else if (truncateEnd) {
        pages = pages.slice(0, pagesVisibleCount - 2);
      }
    }

    return {
      pages,
      truncateStart,
      truncateEnd,
    };
  }, [pagesCount, activePage, pagesVisibleCount]);

  const onClick = useCallback(
    (e: MouseEvent, page: number) => {
      e.preventDefault();

      if (onPageClick && page >= 1 && page <= pagesCount) {
        onPageClick(page);
      }
    },
    [onPageClick, pagesCount]
  );

  const className = {
    arrowLinkClass:
      'flex justify-center items-center py-20px px-20px md:px-30px lg:px-40px no-underline bg-white border-y-3px border-transparent focus:z-10 monochrome:text-black',
    arrowLinkEnabledClass:
      'text-purple80 monochrome:text-black hover:border-b-green70 monochrome:hover:border-b-black wl-border-bottom-highlight--hover',
    arrowLinkDisabledClass:
      'text-lightPurple monochrome:text-grey60 cursor-default',
    pageLinkClass:
      'min-w-[60px] flex-1 flex justify-center items-center p-20px relative no-underline border-y-3px border-transparent focus:z-10 monochrome:text-black',
    pageLinkInactiveClass:
      'bg-white text-purple80 hover:border-b-green70 monochrome:text-grey90 monochrome:hover:border-b-black wl-border-bottom-highlight--hover',
    pageLinkActiveClass:
      'bg-purple80 text-white monochrome:bg-brandColor  monochrome:!text-white font-headline',
  };

  if (pagesCount <= 1) {
    return null;
  }

  return (
    <nav
      className="flex font-regular h-[60px] pagination text-p1 w-full"
      aria-label="Pagination"
    >
      <button
        className={clsx(
          className.arrowLinkClass,
          activePage > 1 && className.arrowLinkEnabledClass,
          activePage === 1 && className.arrowLinkDisabledClass
        )}
        onClick={(e) => onClick(e, activePage - 1)}
      >
        <ArrowLeft16 className="mr-10px" aria-hidden="true" />
        {prevLinkText}
      </button>
      <div className="pagination-navigator">
        {activePage} {navigatorText} {pagesCount}
      </div>
      <div className="pagination-pages">
        {truncateStart && (
          <>
            <PageLink
              page={1}
              className={clsx(
                className.pageLinkClass,
                className.pageLinkInactiveClass,
                'ml-px'
              )}
              onClick={(e) => onClick(e, 1)}
            />
            <Ellipsis />
          </>
        )}
        {pagesCount > 1 &&
          pages.map((p) => (
            <PageLink
              key={p}
              page={p}
              isActive={p === activePage}
              className={clsx(
                className.pageLinkClass,
                'ml-px',
                p !== activePage && className.pageLinkInactiveClass,
                p === activePage && className.pageLinkActiveClass,
                {
                  'mr-px': p === pagesCount,
                }
              )}
              onClick={(e) => onClick(e, p)}
            />
          ))}
        {truncateEnd && (
          <>
            <Ellipsis />
            <PageLink
              page={pagesCount}
              className={clsx(
                className.pageLinkClass,
                className.pageLinkInactiveClass,
                'mx-px'
              )}
              onClick={(e) => onClick(e, pagesCount)}
            />
          </>
        )}
      </div>
      <button
        className={clsx(
          className.arrowLinkClass,
          activePage < pagesCount && className.arrowLinkEnabledClass,
          activePage === pagesCount && className.arrowLinkDisabledClass
        )}
        onClick={(e) => onClick(e, activePage + 1)}
      >
        {nextLinkText}
        <ArrowRight16 className="ml-10px" aria-hidden="true" />
      </button>
    </nav>
  );
};

export default Pagination;
